/* My Easy Bites — product.js (product templates only, deferred) */
(() => {
  /* ---------- Media gallery: swipe, dots, thumbs, video autoplay, lightbox ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.lightbox = this.querySelector('[data-lightbox]');
      this.active = 0;

      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { if (en.isIntersecting) this.setActive(+en.target.dataset.index); });
      }, { root: this.track, threshold: 0.6 });
      this.slides.forEach((s) => io.observe(s));

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(+t.dataset.thumb)));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(Math.min(this.active + 1, this.slides.length - 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(Math.max(this.active - 1, 0)); }
      });
      this.querySelectorAll('[data-zoom]').forEach((b) => b.addEventListener('click', () => this.openLightbox(+b.dataset.zoom)));

      if (this.lightbox) {
        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('click', (e) => {
          if (e.target.classList.contains('lightbox__img')) e.target.classList.toggle('is-zoomed');
        });
      }

      // Variant with featured media → jump to it
      document.addEventListener('meb:variant', (e) => {
        if (e.detail.mediaIndex != null) this.goTo(e.detail.mediaIndex);
      });
      this.playVisibleVideo();
    }
    goTo(i) {
      const slide = this.slides[i];
      if (!slide) return;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      if (i === this.active && this._init) return;
      this._init = true;
      this.active = i;
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current');
      });
      const at = this.thumbs[i];
      if (at && at.parentElement.parentElement.scrollWidth > at.parentElement.parentElement.clientWidth) {
        at.parentElement.parentElement.scrollTo({ left: at.parentElement.offsetLeft - 16, behavior: 'smooth' });
      }
      this.playVisibleVideo();
    }
    playVisibleVideo() {
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slides.forEach((s, n) => {
        const v = s.querySelector('video');
        if (!v) return;
        if (n === this.active && !reduce) {
          v.muted = true; v.playsInline = true; v.loop = true;
          v.play().catch(() => {});
        } else v.pause();
      });
    }
    openLightbox(i) {
      if (!this.lightbox) return;
      window.MEB.openDialog(this.lightbox);
      const target = this.lightbox.querySelector(`[data-lightbox-index="${i}"]`);
      if (target) requestAnimationFrame(() => target.scrollIntoView({ inline: 'center', block: 'nearest' }));
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Sticky add-to-cart bar ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const cta = document.getElementById(this.dataset.target);
      const form = document.getElementById(this.dataset.form);
      if (!cta || !form) return;
      const footer = document.querySelector('.site-footer');
      // Show whenever the main button is off-screen (above OR below), so a buy
      // button is always in view on mobile — including the first screen.
      let ctaOff = !!this.hasAttribute('data-early'), ctaPassed = false, footerIn = false;
      const update = () => {
        const show = (this.hasAttribute('data-early') ? ctaOff : ctaPassed) && !footerIn;
        this.toggleAttribute('data-visible', show);
        this.toggleAttribute('inert', !show);
        this.setAttribute('aria-hidden', String(!show));
      };
      new IntersectionObserver(([en]) => {
        ctaOff = !en.isIntersecting;
        ctaPassed = ctaOff && en.boundingClientRect.top < 0;
        update();
      }).observe(cta);
      if (footer) new IntersectionObserver(([en]) => { footerIn = en.isIntersecting; update(); }).observe(footer);

      this.querySelector('[data-sticky-submit]').addEventListener('click', () => {
        const submit = form.querySelector('[type=submit]');
        form.requestSubmit ? form.requestSubmit(submit) : submit.click();
      });
      const priceEl = this.querySelector('[data-sticky-price]');
      document.addEventListener('meb:variant', (e) => { if (priceEl && e.detail.price) priceEl.textContent = window.MEB.formatMoney(e.detail.price); });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Main price block reacts to variant changes ---------- */
  document.addEventListener('meb:variant', (e) => {
    const { price, compare, id } = e.detail;
    const fromBuyBox = e.detail.source && e.detail.source.closest && e.detail.source.closest('.buy-box');
    const block = document.querySelector('.buy-box [data-price]');
    if (fromBuyBox && block && price) {
      const cur = block.querySelector('[data-price-current]');
      if (cur) cur.textContent = window.MEB.formatMoney(price);
      const cmp = block.querySelector('[data-price-compare]');
      if (cmp && compare > price) cmp.textContent = window.MEB.formatMoney(compare);
    }
    // keep URL shareable for real variants of this product
    if (id && fromBuyBox) {
      const url = new URL(location.href);
      url.searchParams.set('variant', id);
      history.replaceState(null, '', url);
    }
  });

  /* ---------- ViewContent ---------- */
  const pj = document.querySelector('[data-product-json]');
  if (pj) {
    try {
      const p = JSON.parse(pj.textContent);
      window.MEB.track('ViewContent', { variantId: p.variantId, price: p.price, title: p.title });
    } catch (e) {}
  }
})();
